<script setup lang="ts">
import { computed } from 'vue';

import type { OrderEntity } from '@/entities/order';

import { formatFiscalOperationStatus } from '@/entities/order';
import { AppStatusChip } from '@/shared/ui';
import { formatDateTime, inTimezone } from '@shared/lib/date-time';

const props = defineProps<{
  operations: OrderEntity['fiscal_operations'];
}>();
const sorted = computed(() =>
  [...props.operations].sort((left, right) =>
    (right.completed_at ?? right.started_at ?? right.prepared_at).localeCompare(
      left.completed_at ?? left.started_at ?? left.prepared_at,
    ),
  ),
);

function safeReceiptUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}
</script>

<template>
  <section class="rounded-xl bg-(--surface-card) p-4 md:p-6">
    <h2 class="text-base font-semibold text-(--text-primary)">Фискальные операции</h2>
    <p
      v-if="sorted.length === 0"
      class="mt-3 text-sm text-(--text-secondary)"
    >
      Фискальных операций нет.
    </p>
    <div
      v-else
      class="mt-4 flex flex-col gap-3"
    >
      <article
        v-for="operation in sorted"
        :key="operation.id"
        class="rounded-xl border border-(--border) bg-(--surface-sunken) p-4"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p class="text-sm font-semibold text-(--text-primary)">
              {{ operation.operation_type === 'REFUND' ? 'Возврат прихода' : 'Приход' }}
            </p>
            <p class="mt-1 text-xs text-(--text-secondary)">
              {{ operation.workstation.name }} ·
              {{
                formatDateTime(
                  inTimezone(
                    operation.completed_at ?? operation.started_at ?? operation.prepared_at,
                    operation.timezone,
                  ),
                )
              }}
            </p>
          </div>
          <AppStatusChip :tone="formatFiscalOperationStatus(operation).tone">
            {{ formatFiscalOperationStatus(operation).label }}
          </AppStatusChip>
        </div>
        <dl class="mt-3 grid grid-cols-2 gap-3 text-xs md:grid-cols-4">
          <div>
            <dt class="text-(--text-secondary)">ФН</dt>
            <dd class="mt-1 text-(--text-primary)">{{ operation.fiscal_drive_number_masked ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-(--text-secondary)">ФД</dt>
            <dd class="mt-1 text-(--text-primary)">{{ operation.fiscal_document_number ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-(--text-secondary)">ФП</dt>
            <dd class="mt-1 text-(--text-primary)">{{ operation.fiscal_document_sign ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-(--text-secondary)">Смена / чек</dt>
            <dd class="mt-1 text-(--text-primary)">
              {{ operation.shift_number ?? '—' }} / {{ operation.receipt_number ?? '—' }}
            </dd>
          </div>
        </dl>
        <a
          v-if="safeReceiptUrl(operation.receipt_url)"
          class="mt-3 inline-flex min-h-11 items-center text-sm font-medium text-(--accent)"
          :href="safeReceiptUrl(operation.receipt_url) ?? undefined"
          target="_blank"
          rel="noopener noreferrer"
        >
          Открыть электронный чек
        </a>
      </article>
    </div>
  </section>
</template>
